import { useState } from "react";
import { AssessmentActionsSlotContext } from "./AssessmentActions";
import AssessmentSummaryCards from "./AssessmentSummaryCards";

/**
 * Shared shell for the assessments rendered inside the Assessments tab, so
 * every assessment keeps the same surface, spacing and heading treatment.
 *
 * The questionnaire controls are rendered into the slot below the panel, so
 * they stay outside the panel surface while remaining part of the
 * questionnaire's React tree.
 */
const AssessmentPanel = ({
    id,
    title,
    titleBadge,
    subtitle,
    status,
    toolbar,
    summaries,
    summariesLoading = false,
    summariesHidden = false,
    onSummaryAction,
    children
}) => {
    const [actionsSlot, setActionsSlot] = useState(null);
    const hasSummaries =
        !summariesHidden && (summariesLoading || !!summaries?.length);

    return (
        <AssessmentActionsSlotContext.Provider value={actionsSlot}>
            <section id={id} className="assessment-panel">
                <div
                    className={`assessment-panel__header ${
                        hasSummaries ? "has-summaries" : ""
                    }`}
                >
                    <div className="assessment-panel__intro">
                        <div className="assessment-panel__title">
                            <h5 className="mb-0">{title}</h5>
                            {titleBadge && (
                                <span className="assessment-panel__title-badge">
                                    {titleBadge}
                                </span>
                            )}
                        </div>

                        {subtitle && (
                            <p className="text-muted mb-0">{subtitle}</p>
                        )}

                        {status?.label && (
                            <div className="assessment-panel__intro-status">
                                <span className="assessment-panel__status">
                                    {status.label}
                                    {status.percent != null
                                        ? ` · ${status.percent}%`
                                        : ""}
                                </span>
                                {status.description && (
                                    <small className="text-muted d-block mt-1">
                                        {status.description}
                                    </small>
                                )}
                            </div>
                        )}

                        {toolbar && (
                            <div className="assessment-panel__toolbar">
                                {toolbar}
                            </div>
                        )}
                    </div>

                    <AssessmentSummaryCards
                        cards={summaries}
                        isLoading={summariesLoading && !summariesHidden}
                        hidden={summariesHidden}
                        onAction={onSummaryAction}
                    />
                </div>

                {children}
            </section>

            <div ref={setActionsSlot} />
        </AssessmentActionsSlotContext.Provider>
    );
};

export default AssessmentPanel;
